package horcrux.frontend.common.views.collections.collectiondialogs

import arrow.core.none
import arrow.core.some
import arrow.core.toOption
import horcrux.common.HorcruxEither
import horcrux.common.api.dtos.EmbeddingModelID
import horcrux.common.api.dtos.PostCollectionResponse
import horcrux.common.api.dtos.types.CollectionMetadata
import horcrux.common.api.dtos.types.CollectionName
import horcrux.common.utils.eitherWay
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.MaybeState
import horcrux.frontend.common.State
import horcrux.frontend.common.clients.backendclient.AuthenticatedBackendClient
import horcrux.frontend.common.get
import horcrux.frontend.common.handleResponseException
import horcrux.frontend.common.views.collections.CollectionMetadataByID
import horcrux.frontend.common.views.components.containers.DialogAction
import horcrux.frontend.common.views.components.containers.DialogView
import horcrux.frontend.common.views.components.containers.UpdateBanner
import io.ktor.http.HttpStatusCode
import mui.material.AlertColor
import mui.material.DialogContent
import mui.material.DialogTitle
import mui.system.sx
import react.FC
import react.Props
import react.StateInstance
import web.cssom.pt

external interface CreateCollectionDialogProps : Props {
    var showDialogState: BooleanState
    var collectionMetadataByID: CollectionMetadataByID
    var updateCollectionMetadata: (CollectionMetadata) -> Unit
    var closeDialog: () -> Unit

    var globalAppState: GlobalAppState
}

typealias CollectionNameMaybeState = StateInstance<CollectionName?>

fun CollectionNameMaybeState.isInvalidOrNotUnique(collectionMetadataByID: CollectionMetadataByID): Boolean =
    this.get()?.let { !it.isValidAndUnique(collectionMetadataByID.collectionNames) } ?: false

suspend fun AuthenticatedBackendClient.createCollectionAndHandleResponse(
    collectionName: CollectionName,
    embeddingModelID: EmbeddingModelID,
    updateBanner: UpdateBanner,
    updateCollectionMetadata: (CollectionMetadata) -> Unit = {},
    onSuccess: () -> Unit = {},
): HorcruxEither<PostCollectionResponse> =
    this
        .postCollection(collectionName, embeddingModelID)
        .handleResponseException { cause ->
            if (cause.response.status == HttpStatusCode.Conflict) {
                updateBanner(
                    AlertColor.error,
                    "Document collection names must be unique"
                )
                return@handleResponseException none<Throwable>()
            }
            cause.some()
        }
        .map {
            updateBanner(AlertColor.success, "Document collection created!")
            onSuccess()
            updateCollectionMetadata(it.collectionMetadata)
            it
        }

data class CollectionDetailsState(
    val collectionNameMaybeState: MaybeState<CollectionName> = MaybeState(),
    val defaultCollectionNameMaybeState: MaybeState<CollectionName> = MaybeState(),
    val collectionMediaTypeState: State<CollectionMediaType> = State(CollectionMediaType.TEXT_ONLY),
) {
    val isCollectionNameInvalid: Boolean
        get() = collectionNameMaybeState.get()?.isInvalid() ?: false

    var defaultCollectionNameMaybe: CollectionName?
        get() = defaultCollectionNameMaybeState.get()
        set(value) {
            defaultCollectionNameMaybeState.set(value)
            collectionNameMaybeState.set(value)
        }

    fun resetCollectionName() {
        collectionNameMaybeState.set(defaultCollectionNameMaybeState.get())
    }

    fun reset() {
        collectionNameMaybeState.reset()
        defaultCollectionNameMaybeState.reset()
        collectionMediaTypeState.reset()
    }
}

val CreateCollectionDialog = FC<CreateCollectionDialogProps> { props ->
    val collectionDetailsState = CollectionDetailsState()
    val isCollectionNameValid = collectionDetailsState.collectionNameMaybeState.get()?.isValid() ?: true

    DialogView {
        this.title = "Create Document Collection"
        this.showDialogState = props.showDialogState
        this.dialogActionsMaybe = listOf(
            DialogAction("Cancel") { showDialogState, _ -> showDialogState.setFalse() },
            DialogAction("Submit") { showDialogState, isLoadingState ->
                collectionDetailsState
                    .collectionNameMaybeState
                    .get()
                    .toOption()
                    .filter { isCollectionNameValid }
                    .map { collectionName ->
                        props.globalAppState.submitBackendAPIRequestAsync { backendClient ->
                            isLoadingState.setTrue()
                            backendClient.createCollectionAndHandleResponse(
                                collectionName,
                                collectionDetailsState.collectionMediaTypeState.get().embeddingModelID,
                                props.globalAppState.updateBanner,
                                props.updateCollectionMetadata
                            ) { showDialogState.setFalse() }
                                .eitherWay { isLoadingState.setFalse() }
                                .bind()
                        }
                    }
            }
        )
        DialogTitle {
            +"Create a Collection"
        }
        DialogContent {
            this.sx {
                this.marginRight = 10.pt
                this.marginLeft = 10.pt
            }
            EditCollectionSettings {
                this.collectionDetailsState = collectionDetailsState
            }
        }
    }
}
